import { TinyDropDownOption } from '~components/common/TinyDropDown';
import { COMPANY_DETAIL_MENU } from '~constant/company';

import { KeysOf, Nullable, ValueOf } from './common';

export interface ISalaryResponse {
  avg: ISalaryAvg[];
  defaultSource: COMPANYS_SOURCE_KEY;
  salaries: ISalaries;
}

export type ISalaries = {
  [key in COMPANYS_SOURCE_KEY]: ISalary[];
};

export interface ISalaryAvg {
  category: 'HIRED' | 'ALL';
  industry: string | number;
  total: string | number;
}
export interface ISalary {
  category: 'ALL' | 'HIRED';
  industryRate: number;
  rateType: 'INDUSTRY' | 'TOTAL';
  salary: number | string;
  totalRate: number;
  updatedAt: Date | null;
}

export type ISalaryInfo = ISalary & ISalaryAvg;

export interface CompanyStates {
  board: boolean;
  closed: boolean;
  closedAt: Date | string | null;
  deleted: boolean;
  employee: boolean;
  rank: boolean;
  salary: boolean;
  sales: boolean;
}

export const COMPANY_SOURCE = {
  // 알리오 > 클린아이 > 금감원 > 국민연금 > 고용보험
  CORP: '기업 제공',
  ALIO: '알리오',
  EYE: '클린아이',
  FSS: '금감원',
  NPS: '국민연금',
} as const;

export const SalesSource = {
  CORP: '기업 제공',
  FSS: '금감원',
  NICE: 'NICE',
} as const;

export type SalesSourceKey = keyof typeof SalesSource;
export type SalesSourceValue = ValueOf<typeof SalesSource>;
export type COMPANYS_SOURCE_VALUE = ValueOf<typeof COMPANY_SOURCE>;
export type COMPANYS_SOURCE_KEY = keyof typeof COMPANY_SOURCE;
export type PickedCompanySource = Pick<
  typeof COMPANY_SOURCE,
  'CORP' | 'ALIO' | 'EYE' | 'FSS' | 'NPS' | 'EI'
>;

export type PickedCompanySourceKey = KeysOf<PickedCompanySource>;

export type ICompanySourceKey = KeysOf<typeof COMPANY_SOURCE>;

export type CompanyBase = {
  id: number;
  PK_NM_HASH: string;
  logo: string;
  name: string;
  locationCode: string;
  location: string;
  niceId: string;
};

export type CompanyInfo = {
  categoryId: number;
  category: COMPANYS_SOURCE_KEY;
  industryCode: string;
  foundedYear: number;
  age: number;
};

export type SummarySalary = {
  salary: number;
  rate: number;
  rateType: 'INDUSTRY' | 'TOTAL';
  source: COMPANYS_SOURCE_KEY;
  updatedAt: null | string;
};

export type SummaryEmployee = {
  total: number;
  hired: number;
  left: number;
  leftRate: number;
  hiredRate: number;
  source: COMPANYS_SOURCE_KEY;
  updatedAt: null | string;
  rate: number;
  rateType: 'INDUSTRY' | 'TOTAL';
};

export type SummarySales = {
  total: number;
  perPerson: number;
  salesSalaryRate: number;
  source: SalesSourceKey;
  updatedAt: null | string;
  rate: number;
  rateType: 'INDUSTRY' | 'TOTAL';
};
export type CompanySummary = {
  salary: SummarySalary;
  employee: SummaryEmployee;
  sales: SummarySales;
};

export const COMPANY_TYPE: { [key in string]: string } = {
  KOSPI: '코스피',
  KOSDAQ: '코스닥',
  KONEX: '코넥스',
  CORP: '일반기업',
  DEFAULT: '공공기관',
} as const;

export type CompanyEmployee = {
  updatedAt: string | Date;
  hired: Employee;
  left: Employee;
  total: Employee;
};

export type Employee = {
  employee: Nullable<number>;
  rate: Nullable<number>;
  rateType: Nullable<'INDUSTRY' | 'TOTAL'>;
  industryRate: Nullable<number>;
  industryAvgRate: Nullable<number>;
  industryEmployee: Nullable<number>;
  totalRate: Nullable<number>;
  totalAvgRate: Nullable<number>;
  totalEmployee: Nullable<number>;
};

export type RateAvg = {
  type: 'hired' | 'left' | 'total';
  employee: Nullable<number>;
  avgRate: Nullable<number>;
  totalAvgRate: Nullable<number>;
  rate: Nullable<number>;
  employeeCount: Nullable<number>;
};

export type WIKI_NEW_EMPLOYEE_CATEGORY = 'HIGHSCHOOL' | 'COLLEGE' | 'UNIVERSITY' | 'MASTER';
export type WIKI_CAREER_EMPLOYEE_CATEGORY = 'JIDR' | 'GJ' | 'CJ' | 'BJ';

export type WIKI_CATEGORY_KEY = WIKI_NEW_EMPLOYEE_CATEGORY | WIKI_CAREER_EMPLOYEE_CATEGORY;

export enum WIKI_CATEGORY_TITLE {
  HIGHSCHOOL = '고졸',
  COLLEGE = '초대졸',
  UNIVERSITY = '대졸',
  MASTER = '대학원졸',
  JIDR = '2~4년',
  GJ = '5~7년',
  CJ = '8~10년',
  BJ = '10년 초과',
}

export type IWikiChart = {
  title: WIKI_CATEGORY_TITLE;
  data: number;
  stakeData: { min: number; max: number };
  showData: { avg: number; min: number; max: number; count: number };
  isDataEmpty: boolean;
}[];

export type RegisterRef = (element: HTMLElement, id: ValueOf<typeof COMPANY_DETAIL_MENU>) => void;

export interface SalesResponse {
  defaultSource: SalesSourceKey;
  sales: SalesInfo;
}

export type SalesInfo = {
  [key in keyof typeof SalesSource]: {
    perPerson: Sales;
    salesSalaryRate: Sales;
    total: Sales;
    updatedAt: Date | null;
  };
};

export interface Sales {
  industryRate: number | null;
  rateType: 'INDUSTRY' | 'TOTAL';
  sales: number | null;
  totalRate: number | null;
}

export interface ITransformSales {
  isEmptySales: boolean;
  sourceOption: TinyDropDownOption[];
  defaultSource: SalesSourceKey;
  activeSales: {
    perPerson: Sales;
    salesSalaryRate: Sales;
    total: Sales;
    updatedAt: Date | null;
  };
}

export interface ICompanyAutoComplete {
  docs: ICompany[];
  numFound: number;
}

export interface ICompany {
  PK_NM_HASH: string;
  name: string;
  location: string;
  locationCode: string;
  companyId: number;
}

export interface IndustryItem {
  code: string;
  name: string;
}

export interface IWorkLifeObj {
  count: number;
  rate: number;
}

export interface ICompanyWorkLife {
  overtime: {
    notatall: IWorkLifeObj;
    onetotwo: IWorkLifeObj;
    threetofour: IWorkLifeObj;
    daily: IWorkLifeObj;
  };
  weekend: {
    notatall: IWorkLifeObj;
    sometimes: IWorkLifeObj;
    everyday: IWorkLifeObj;
  };
}
